import { createSelector } from 'reselect';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';

import { ENTITIES_KEY_NODE, NODE_TYPE_QUESTION } from './index';
import {
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP,
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP_REGEXP,
    NODE_NAME_GUEST_INFORMATION,
    NODE_NAME_SOCIAL_RSVP,
} from './nodeNames';
import { INPUT_TYPE_CAPTCHA } from './inputTypes';
import { NODE_TYPE_ROOT } from './nodeTypes';

import { getForm, getAllForms } from '../form/selectors';
import { getEntitySelector } from '../selectors';

/**
 * @param {Object} state
 * @param {string} formId
 * @param {string} name
 * @return {Object}
 */
export const getFormNodeByName = (state, formId, name) => {
    const formNodes = getEntitySelector(state, ENTITIES_KEY_NODE);
    const formNodeId = Object.keys(formNodes).find(
        (id) => formNodes[id].name === name && formNodes[id].formId === formId,
    );
    return formNodeId ? formNodes[formNodeId] : undefined;
};

/**
 * ⚠️ This returns all entities, including orphan ones.
 * @param {Object} state
 * @param {Object} - object with entities
 */
export const getAllFormNodeEntities = (state) => getEntitySelector(state, ENTITIES_KEY_NODE) || {};

/**
 * @param  {Object} state
 * @param  {string} formId - form id
 * @return {Object}
 */
export const getRootFormNodeId = (state, formId) => {
    const form = getForm(state, formId);
    return get(form, NODE_TYPE_ROOT, '');
};

/**
 * Iterates through the given formNode children and returns an array with all the
 * active nodes for the actual form owning the received formNode.
 *
 * @param {Object} formNode
 * @param {Object} allFormNodes
 * @return {Array}
 */
export const getActiveChildrenNodes = (formNode, allFormNodes) =>
    ((formNode && formNode.children) || []).reduce((acc, childNodeId) => {
        const childNode = allFormNodes[childNodeId];
        if (!childNode) {
            return acc;
        }

        return ((childNode && childNode.children) || []).length
            ? [...acc, childNode, ...getActiveChildrenNodes(childNode, allFormNodes)]
            : [...acc, childNode];
    }, []);

/**
 * Retrieves all the nodes for a given form and filter it's children using the root node.
 *
 * @param {Object} state
 * @param {string} formId
 * @return {Array}
 */
export const getFormNodes = createSelector(
    getAllFormNodeEntities,
    getRootFormNodeId,

    (allFormNodes, rootNodeId) => {
        const rootNode = allFormNodes[rootNodeId];
        if (!rootNode) {
            return [];
        }
        const childNodes = getActiveChildrenNodes(rootNode, allFormNodes);
        return [rootNode, ...childNodes];
    },
);

/**
 * @param {Object} state
 * @return {Object[]} - array of form nodes
 */
export const getAllFormNodes = (state) => {
    const forms = getAllForms(state);
    return flatMap(forms, (form) => getFormNodes(state, form.id));
};

/**
 * @param  {Object} state
 * @param  {string} nodeId
 * @return {Object}
 */
export const getFormNode = createSelector(
    [getAllFormNodeEntities, (state, nodeId) => nodeId],
    (formNodes, nodeId) => formNodes[nodeId],
);

/**
 * @param  {Object} state
 * @param  {string} nodeId
 * @return {Object[]}
 */
export const getFormNodeConfigValue = (state, nodeId, field) => {
    const formNode = getFormNode(state, nodeId);
    return get(formNode, ['config', field]);
};

/**
 * @param  {Object} state
 * @param  {string} nodeId
 * @return {Object[]}
 */
export const getFormNodeOptions = (state, nodeId) => {
    const formNode = getFormNode(state, nodeId);
    const entities = get(formNode, 'config.options.entities', {});
    const result = get(formNode, 'config.options.result', []);
    return result.map((id) => entities[id]);
};

/**
 * @param  {Object} state
 * @param  {string} nodeId
 * @return {string|undefined} - label from config entities
 */
export const getFormNodeOptionLabel = (state, nodeId, optionId) => {
    const formNode = getFormNode(state, nodeId);
    const entities = get(formNode, 'config.options.entities');
    return get(entities, `${optionId}.label`);
};

/**
 * @param  {Object} state
 * @param  {string} nodeId
 * @return {string|undefined} - label from config entities
 */
export const getFormNodeOptionValue = (state, nodeId, optionId) => {
    const formNode = getFormNode(state, nodeId);
    const entities = get(formNode, 'config.options.entities');
    return get(entities, `${optionId}.value`);
};

/**
 * @param  {Object} state
 * @param  {string} formId - form id
 * @return {Object[]}
 */
export const getQuestionFormNodes = createSelector([getFormNodes], (formNodes) =>
    formNodes.filter((formNode) => formNode.type === NODE_TYPE_QUESTION),
);

/**
 * @param  {Object} state
 * @param  {string} formId - form id
 * @param  {string} inputType
 * @return {Object[]}
 */
export const getQuestionFormNodesOfInputType = createSelector(
    [getFormNodes, (state, formId, inputType) => inputType],
    (formNodes, inputType) =>
        formNodes.filter((formNode) => formNode.type === NODE_TYPE_QUESTION && formNode.inputType === inputType),
);

/**
 * @param {Object} state
 * @param {string} nodeId
 * @return {bool}
 */
export const getFormNodeIncludeOther = (state, nodeId) => {
    const node = getFormNode(state, nodeId);
    return node && node.config && node.config.includeOther;
};

/**
 * @param  {Object} state
 * @param  {string} formId - form id
 * @return {Object[]}
 */
export const hasFormNodesResponseLimit = createSelector(
    getFormNodes,
    (formNodes) =>
        !!formNodes.find(
            (node) =>
                node.config &&
                node.config.options &&
                !!Object.values(node.config.options.entities).find((option) => !!option.responseLimit),
        ),
);

/**
 * Returns an array of direct children ids of the given node by id.
 * @param  {Object} state
 * @param  {Object} formNode - formNode
 * @return {Object[]}
 */
export const getChildrenFromNodeId = (state, formNodeId) => {
    const formNode = getFormNode(state, formNodeId);
    return formNode.children.map((childId) => getFormNode(state, childId));
};

export const getChildrenFromNodeIdByInputType = createSelector(
    getChildrenFromNodeId,
    (state, formNodeId, type) => type,
    (children, type) => children.filter((child) => child.inputType === type),
);

const getAllChildrenIds = (state, nodeIds) =>
    nodeIds.reduce((acc, nodeId) => {
        const node = getFormNode(state, nodeId);
        return [...acc, nodeId, ...(node.children.length ? getAllChildrenIds(state, node.children) : [])];
    }, []);

/**
 * Gets ids of all nodes in the children tree of the given node by id.
 * @param {Object} state
 * @param {string} formNodeId
 * @return {string[]} - all children ids
 */
export const getChildrenIdsRecursivelyFromNodeId = (state, formNodeId) => {
    const allIds = getAllChildrenIds(state, [formNodeId]);
    const allChildrenIds = allIds.slice(1);
    return allChildrenIds;
};

/**
 * @param {Object} state
 * @param {string} nodeId
 * @return {string}
 */
export const getFormNodeUrlParamName = (state, nodeId) => {
    const formNode = getFormNode(state, nodeId);
    return formNode && formNode.urlParamName;
};

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Object[]} - additional guest information groups
 */
export const getAdditionalGuestInfoGroups = (state, formId) => {
    const formNodeEntities = getEntitySelector(state, ENTITIES_KEY_NODE);
    return Object.values(formNodeEntities).filter(
        (formNode) => formNode.formId === formId && NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP_REGEXP.test(formNode.name),
    );
};

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Object} - additional guest count question node
 */
export const getAdditionalGuestCountQuestion = (state, formId) => {
    const formNodeEntities = getEntitySelector(state, ENTITIES_KEY_NODE);
    return Object.values(formNodeEntities).find(
        (formNode) => formNode.formId === formId && formNode.name === NODE_NAME_ADDITIONAL_GUEST_COUNT,
    );
};

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Object[]} - additional guest information groups
 */
export const getAdditionalGuestInfoGroupsButFirst = (state, formId) => {
    const groups = getAdditionalGuestInfoGroups(state, formId);
    const index = groups.findIndex((node) => node.name === NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP);
    return [...groups.slice(0, index), ...groups.slice(index + 1)];
};
/**
 * @param {Object} state
 * @param {string} formId
 * @return {Object} - additional guest information group
 */
export const getFirstAdditionalGuestInfoGroup = (state, formId) => {
    const formNodeEntities = getEntitySelector(state, ENTITIES_KEY_NODE);
    return Object.values(formNodeEntities).find((formNode) => formNode.name === NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP);
};

export const getCaptchaQuestions = createSelector(getFormNodes, (formNodes) =>
    formNodes.filter((node) => node.inputType === INPUT_TYPE_CAPTCHA),
);

/**
 * @param {Object} state
 * @param {string} formId
 * @param {string} code
 * @return {Object[]} - form nodes with the given code
 */
export const getFormNodesByCode = createSelector([getFormNodes, (state, formId, code) => code], (formNodes, code) =>
    formNodes.filter((node) => node.code === code),
);

/**
 * @param {Object} state
 * @param {string} formId
 * @param {string} code
 * @return {Object} - form node with the given code
 */
export const getFirstFormNodeByCode = (state, formId, code) => {
    const nodes = getFormNodesByCode(state, formId, code);
    return nodes.length > 0 ? nodes[0] : undefined;
};

/**
 * @param {Object} state
 * @param {string} formNodeId
 * @return {bool} - true if node editing is locked
 */
export const getFormNodeEditingLocked = (state, formNodeId) => {
    const formNode = getFormNode(state, formNodeId);
    return formNode ? parseInt(formNode.lock, 10) >= 1 : false; // returning false if no node makes selector safer, but problems less debuggable.
};

/**
 * Return the first plus one group in the node tree.
 *
 * @param {Object} state
 * @param {string} formId
 * @return {Object}
 */
export const getFirstPlusOneGroup = createSelector(getFormNodes, (formNodes) =>
    formNodes.find((node) => node.name === NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP),
);

const getSocialRsvpGroupConfig = (state, formId, socialNetwork) => {
    const socialRsvpNode = getFormNodeByName(state, formId, NODE_NAME_SOCIAL_RSVP);

    return get(socialRsvpNode, ['config', socialNetwork]);
};

export const getSelectedSocialRsvp = (state, formId) => {
    const socialNetworks = get(getFormNodeByName(state, formId, NODE_NAME_SOCIAL_RSVP), 'config', {});

    return Object.keys(socialNetworks).find((socialNetwork) => get(socialNetworks[socialNetwork], 'enabled', false));
};

export const getSocialRsvpGroupEnabled = createSelector(getSocialRsvpGroupConfig, (socialNetworkConfig) =>
    get(socialNetworkConfig, 'enabled'),
);

export const getSocialRsvpFieldPrepopulatedId = (state, formId, fieldId) => {
    const socialRsvpNode = getFormNodeByName(state, formId, NODE_NAME_SOCIAL_RSVP);

    return get(socialRsvpNode, ['fields', fieldId, 'prepopulateNodeId'], null);
};

export const getSocialQuestionByPrepopulatedId = (state, formId, target) => {
    const socialRsvpNode = getFormNodeByName(state, formId, NODE_NAME_SOCIAL_RSVP);
    const fields = get(socialRsvpNode, ['fields'], []);

    return Object.keys(fields).filter((key) => get(fields, [key, 'prepopulateNodeId'], null) === target);
};

/**
 * @param {Object} state
 * @param {string} formId
 * @return {string} guest information group id
 */
export const getGuestInformationGroupId = (state, formId) =>
    get(getFormNodeByName(state, formId, NODE_NAME_GUEST_INFORMATION), 'id');
